// Paused uploads of this browser in IndexedDB (idb-keyval), one database per kind of upload (source files, capability documents): a record per file fingerprint with the upload id, name, size, sha256 and expiry, so a reload or another visit resumes from the parts the server already has; every read and write is wrapped so a blocked or private-mode storage only loses the convenience.
import { createStore, del, entries, get, set, type UseStore } from "idb-keyval";

export interface PausedUpload {
  fingerprint: string;
  uploadId: string;
  fileName: string;
  sizeBytes: number;
  sha256: string | null;
  expiresAt: string;
  savedAt: string;
}

export interface UploadRecordStore {
  get: (fingerprint: string) => Promise<PausedUpload | undefined>;
  put: (record: PausedUpload) => Promise<void>;
  remove: (fingerprint: string) => Promise<void>;
  list: () => Promise<PausedUpload[]>;
}

function live(record: PausedUpload, now: number): boolean {
  return Date.parse(record.expiresAt) > now;
}

export function browserRecordStore(databaseName: string): UploadRecordStore {
  let database: UseStore | null = null;
  const idb = (): UseStore => {
    database ??= createStore(databaseName, "paused");
    return database;
  };
  return {
    async get(key) {
      try {
        const record = await get<PausedUpload>(key, idb());
        return record !== undefined && live(record, Date.now()) ? record : undefined;
      } catch {
        return undefined;
      }
    },
    async put(record) {
      try {
        await set(record.fingerprint, record, idb());
      } catch {
        return;
      }
    },
    async remove(key) {
      try {
        await del(key, idb());
      } catch {
        return;
      }
    },
    async list() {
      try {
        const now = Date.now();
        const all = await entries<string, PausedUpload>(idb());
        return all
          .map(([, record]) => record)
          .filter((record) => live(record, now))
          .sort((left, right) => right.savedAt.localeCompare(left.savedAt));
      } catch {
        return [];
      }
    },
  };
}

export const browserUploadStore: UploadRecordStore = browserRecordStore("gridcode-uploads");

export function memoryUploadStore(initial: readonly PausedUpload[] = []): UploadRecordStore {
  const records = new Map(initial.map((record) => [record.fingerprint, record]));
  return {
    get: (key) => Promise.resolve(records.get(key)),
    put: (record) => {
      records.set(record.fingerprint, record);
      return Promise.resolve();
    },
    remove: (key) => {
      records.delete(key);
      return Promise.resolve();
    },
    list: () => Promise.resolve([...records.values()]),
  };
}
